/**
 * Multi-step admin wizard for practitioner role transitions.
 *
 * Product rules:
 * - Capabilities follow the held role set: removing Provider ends treating unless
 *   another held role (Supervisor) still grants it.
 * - Leaving treating / supervising is a hard closeout: every client, appointment,
 *   supervisee, and group facilitator slot needs an explicit disposition first.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Stepper,
  Text,
  Tooltip,
} from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { formatUsDateTime } from '@/lib/unifyDateFormat';
import type { Role, User } from '@/pages/users/types/rbac';
import {
  analyzeRoleTransition,
  executeRoleTransition,
  reactivateUser,
  type RoleTransitionImpactReport,
  type RoleTransitionRequest,
} from '@/lib/adminActionsService';
import {
  capabilityLossFromBaseline,
  closeoutRequiredMessage,
  countUnresolvedReplacements,
  isEligibleReplacementProvider,
  isEligibleReplacementSupervisor,
} from '@/lib/roleTransitionPolicy';
import {
  collapseRolesForDisplay,
  formatCollapsedRoleLabels,
  holdsSupervisingCapability,
  holdsTreatingCapability,
  normalizeHeldRoles,
  roleDisplayLabel,
} from '@unify/fhir-model/role-display';
import { config } from '@/config';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';
import { formatRoleTransitionErrors } from './roleTransitionErrorMessage';

export interface RoleTransitionModalProps {
  opened: boolean;
  onClose: () => void;
  practitionerId: string;
  practitionerName?: string;
  currentRole: Role;
  targetRole: Role;
  /** Full target held role set when driven by multi-role Permissions UI. */
  initialHeldRoles?: Role[];
  /** Held role set before the edit; drives closeout detection for stacked clinical codes. */
  baselineHeldRoles?: Role[];
  allUsers: User[];
  onCompleted: (role: Role) => void | Promise<void>;
  /**
   * `offboard` = Deactivate user closeout (empty roles, matching-pool, cancel-all appts).
   * `reactivate` = Restore inactive account with explicit role assignment.
   * Default `role-change`.
   */
  mode?: 'role-change' | 'offboard' | 'reactivate';
  /** Roles the editor may assign (required for `reactivate`). */
  assignableRoles?: Role[];
}

function snapshotHeld(roles: Role[] | undefined, fallback: Role): Role[] {
  return normalizeHeldRoles(roles ?? [fallback]) as Role[];
}

type StepId = 'review' | 'impact' | 'supervision' | 'caseload' | 'confirm' | 'done';

type ClientDispositionKind = 'transfer' | 'discharge' | 'matching-pool';

const CLIENT_DISPOSITIONS_ROLE_CHANGE = [
  { value: 'transfer', label: 'Transfer' },
  { value: 'discharge', label: 'Discharge' },
];

const CLIENT_DISPOSITIONS_OFFBOARD = [
  { value: 'transfer', label: 'Transfer' },
  { value: 'matching-pool', label: 'Send to matching pool' },
  { value: 'discharge', label: 'Discharge' },
];

const APPOINTMENT_DISPOSITIONS = [
  { value: 'transfer', label: 'Transfer' },
  { value: 'reschedule', label: 'Queue reschedule' },
  { value: 'cancel', label: 'Cancel' },
];

function dispositionOf<T extends string>(
  map: Record<string, T>,
  id: string,
  fallback: T,
): T {
  return map[id] ?? fallback;
}

function bulkSet<T>(
  ids: readonly string[],
  value: T,
): Record<string, T> {
  return Object.fromEntries(ids.map((id) => [id, value]));
}

export function RoleTransitionModal({
  opened,
  onClose,
  practitionerId,
  practitionerName,
  currentRole,
  targetRole,
  initialHeldRoles,
  baselineHeldRoles,
  allUsers,
  onCompleted,
  mode = 'role-change',
  assignableRoles = [],
}: RoleTransitionModalProps): JSX.Element {
  const isOffboard = mode === 'offboard';
  const isReactivate = mode === 'reactivate';
  const medplum = useMedplum();
  const [stepId, setStepId] = useState<StepId>('review');
  const [reactivateRole, setReactivateRole] = useState<Role | null>(null);
  const [impact, setImpact] = useState<RoleTransitionImpactReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [superviseeReplacements, setSuperviseeReplacements] = useState<Record<string, string>>({});
  const [clientReplacements, setClientReplacements] = useState<Record<string, string>>({});
  const [clientKinds, setClientKinds] = useState<Record<string, ClientDispositionKind>>({});
  const [apptKinds, setApptKinds] = useState<Record<string, 'transfer' | 'reschedule' | 'cancel'>>({});
  const [apptReplacements, setApptReplacements] = useState<Record<string, string>>({});
  const [groupReplacements, setGroupReplacements] = useState<Record<string, string>>({});
  const [groupActions, setGroupActions] = useState<Record<string, 'replace' | 'remove'>>({});
  const [resultSummary, setResultSummary] = useState<string | null>(null);

  const sessionRef = useRef<{
    heldBefore: Role[];
    heldAfter: Role[];
    targetRole: Role;
  } | null>(null);
  if (!opened) {
    sessionRef.current = null;
  } else if (!sessionRef.current) {
    sessionRef.current = {
      heldBefore: snapshotHeld(baselineHeldRoles, currentRole),
      heldAfter: isOffboard ? [] : snapshotHeld(initialHeldRoles, targetRole),
      targetRole: isOffboard ? currentRole : targetRole,
    };
  }
  const heldBefore = sessionRef.current?.heldBefore ?? snapshotHeld(baselineHeldRoles, currentRole);
  const heldAfter = isOffboard
    ? []
    : isReactivate
      ? (reactivateRole ? (normalizeHeldRoles([reactivateRole]) as Role[]) : [])
      : (sessionRef.current?.heldAfter ?? snapshotHeld(initialHeldRoles, targetRole));
  const sessionTargetRole = isReactivate
    ? (reactivateRole ?? currentRole)
    : (sessionRef.current?.targetRole ?? (isOffboard ? currentRole : targetRole));

  // Capabilities are a consequence of the resulting role set, never a separate choice.
  const treatsClients = isOffboard ? false : holdsTreatingCapability(heldAfter);
  const supervises = isOffboard ? false : holdsSupervisingCapability(heldAfter);
  const { leavingTreating, leavingSupervising } = capabilityLossFromBaseline({
    baselineHeld: heldBefore,
    treatsClients,
    supervises,
  });

  const removedRoles = collapseRolesForDisplay(heldBefore).filter((r) => !heldAfter.includes(r as Role));
  const addedRoles = collapseRolesForDisplay(heldAfter).filter((r) => !heldBefore.includes(r as Role));

  const primaryAfterLabel = isOffboard
    ? 'Deactivated'
    : isReactivate
      ? (reactivateRole ? roleDisplayLabel(reactivateRole) : 'Select a role')
      : heldAfter.length > 0
        ? roleDisplayLabel(sessionTargetRole)
        : 'None';

  const clientDispositionOptions = isOffboard ? CLIENT_DISPOSITIONS_OFFBOARD : CLIENT_DISPOSITIONS_ROLE_CHANGE;

  const providerOptions = useMemo(
    () =>
      allUsers
        .filter((u) => isEligibleReplacementProvider(u, practitionerId))
        .map((u) => ({ value: u.id, label: u.name || u.email || u.id })),
    [allUsers, practitionerId],
  );

  const supervisorOptions = useMemo(
    () =>
      allUsers
        .filter((u) => isEligibleReplacementSupervisor(u, practitionerId))
        .map((u) => ({ value: u.id, label: u.name || u.email || u.id })),
    [allUsers, practitionerId],
  );

  const displayNameFor = useCallback(
    (id?: string, fallbackDisplay?: string): string => {
      if (fallbackDisplay) return fallbackDisplay;
      if (!id) return '—';
      const match = allUsers.find((u) => u.id === id);
      return match?.name || match?.email || id;
    },
    [allUsers],
  );

  useEffect(() => {
    if (!opened) return;
    setStepId('review');
    setImpact(null);
    setError(null);
    setResultSummary(null);
    setSuperviseeReplacements({});
    setClientReplacements({});
    setClientKinds({});
    setApptKinds({});
    setApptReplacements({});
    setGroupReplacements({});
    setGroupActions({});
    setReactivateRole(null);
  }, [opened]);

  const supervisees = impact?.supervisees ?? [];
  const activeClients = impact?.activeClients ?? [];
  const futureAppointments = impact?.futureAppointments ?? [];
  const groupRoles = impact?.groupFacilitatorRoles ?? [];
  const pendingCosignTotal = supervisees.reduce((n, s) => n + s.pendingCosignCount, 0);

  const clientKind = (id: string) => dispositionOf(clientKinds, id, 'transfer' as const);
  const apptKind = (id: string) => dispositionOf(apptKinds, id, 'transfer' as const);

  const losingSupervision = leavingSupervising && supervisees.length > 0;
  const needsSuperviseeReassign = (leavingSupervising || isOffboard) && supervisees.length > 0;
  const needsSupervisionStep = isOffboard || losingSupervision || needsSuperviseeReassign;
  const needsCaseloadStep =
    leavingTreating && (activeClients.length > 0 || futureAppointments.length > 0 || groupRoles.length > 0);

  const stepIds = useMemo<StepId[]>(() => {
    if (isReactivate) return ['review', 'confirm'];
    const ids: StepId[] = ['review', 'impact'];
    if (needsSupervisionStep) ids.push('supervision');
    if (needsCaseloadStep) ids.push('caseload');
    ids.push('confirm');
    return ids;
  }, [isReactivate, needsSupervisionStep, needsCaseloadStep]);

  const activeIndex = stepId === 'done' ? stepIds.length : Math.max(0, stepIds.indexOf(stepId));
  const goToStep = (offset: number) => {
    const next = stepIds[activeIndex + offset];
    if (next) setStepId(next);
  };

  const groupActionFor = (groupId: string, role: string): 'replace' | 'remove' =>
    groupActions[groupId] ?? (role === 'primary-facilitator' ? 'replace' : 'remove');

  const missingSuperviseeReplacements = needsSuperviseeReassign
    ? countUnresolvedReplacements(
        supervisees.map((s) => ({
          requiresReplacement: true,
          replacementId: superviseeReplacements[s.id],
        })),
      )
    : 0;

  const missingCaseloadReplacements = needsCaseloadStep
    ? countUnresolvedReplacements([
        ...activeClients.map((c) => ({
          requiresReplacement: clientKind(c.episodeOfCareId) === 'transfer',
          replacementId: clientReplacements[c.episodeOfCareId],
        })),
        ...(isOffboard
          ? []
          : futureAppointments.map((a) => ({
              requiresReplacement: apptKind(a.id) === 'transfer',
              replacementId: apptReplacements[a.id],
            }))),
        ...groupRoles.map((g) => ({
          requiresReplacement: groupActionFor(g.groupId, g.role) === 'replace',
          replacementId: groupReplacements[g.groupId],
        })),
      ])
    : 0;

  const closeoutMessage = impact
    ? closeoutRequiredMessage({
        activeClients: leavingTreating ? activeClients.length : 0,
        futureAppointments: leavingTreating ? futureAppointments.length : 0,
        supervisees: leavingSupervising ? supervisees.length : 0,
        pendingCosignatures: leavingSupervising ? pendingCosignTotal : 0,
      })
    : null;

  const affectedImpactCounts = impact
    ? [
        ...(leavingSupervising
          ? [
              { label: 'Supervisees', count: supervisees.length },
              { label: 'Pending cosignatures', count: pendingCosignTotal },
            ]
          : []),
        ...(leavingTreating
          ? [
              { label: 'Open episodes', count: activeClients.length },
              { label: 'Future appointments', count: futureAppointments.length },
              { label: 'Group facilitator roles', count: groupRoles.length },
              { label: 'Open clinical tasks', count: impact.openClinicalTasks.length },
            ]
          : []),
      ].filter((item) => item.count > 0)
    : [];

  const buildRequest = (): RoleTransitionRequest => ({
    practitionerId,
    targetDefaultRole: sessionTargetRole,
    targetFunctionalRoles: heldAfter,
    treatsClients,
    supervisesPractitioners: supervises,
    ...(isOffboard
      ? {
          mode: 'offboard' as const,
          accountDeactivation: { reasonCode: 'provider_termination' },
        }
      : {}),
    superviseeDispositions: needsSuperviseeReassign
      ? supervisees.map((s) => ({
          superviseeId: s.id,
          replacementSupervisorId: superviseeReplacements[s.id] || null,
        }))
      : undefined,
    clientDispositions: leavingTreating
      ? activeClients.map((c) => ({
          episodeOfCareId: c.episodeOfCareId,
          patientId: c.patientId,
          kind: clientKind(c.episodeOfCareId),
          replacementProviderId:
            clientKind(c.episodeOfCareId) === 'transfer'
              ? clientReplacements[c.episodeOfCareId]
              : undefined,
        }))
      : undefined,
    appointmentDispositions:
      leavingTreating && !isOffboard
        ? futureAppointments.map((a) => ({
            appointmentId: a.id,
            kind: apptKind(a.id),
            replacementProviderId:
              apptKind(a.id) === 'transfer' ? apptReplacements[a.id] : undefined,
          }))
        : undefined,
    groupFacilitatorDispositions: leavingTreating
      ? groupRoles.map((g) => ({
          groupId: g.groupId,
          replacementFacilitatorId:
            groupActionFor(g.groupId, g.role) === 'remove' ? undefined : groupReplacements[g.groupId],
          action: g.role === 'primary-facilitator' ? 'replace' : groupActionFor(g.groupId, g.role),
        }))
      : undefined,
    operationId: impact?.operationId,
  });

  const loadImpact = async () => {
    setLoading(true);
    setError(null);
    try {
      const report = await analyzeRoleTransition(medplum, {
        practitionerId,
        targetDefaultRole: sessionTargetRole,
        targetFunctionalRoles: heldAfter,
        treatsClients,
        supervisesPractitioners: supervises,
        ...(isOffboard ? { mode: 'offboard' as const } : {}),
      });
      setImpact(report);
      setClientKinds(bulkSet(report.activeClients.map((c) => c.episodeOfCareId), 'transfer'));
      if (!isOffboard) {
        setApptKinds(bulkSet(report.futureAppointments.map((a) => a.id), 'transfer'));
      }
      setStepId('impact');
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'Failed to analyze role transition'));
    } finally {
      setLoading(false);
    }
  };

  const runReactivate = async () => {
    if (!reactivateRole) {
      setError('Select a role to reactivate this user.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await reactivateUser(medplum, {
        userId: practitionerId,
        role: reactivateRole,
      });
      setResultSummary(`${practitionerName ?? 'This user'} was reactivated as ${roleDisplayLabel(reactivateRole)}.`);
      setStepId('done');
      await onCompleted(reactivateRole);
      notify({
        title: 'User reactivated',
        message: `${practitionerName ?? 'This person'} can sign in again as ${roleDisplayLabel(reactivateRole)}.`,
        type: 'success',
      });
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'Failed to reactivate user'));
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await executeRoleTransition(medplum, buildRequest());
      if (result.status !== 'completed') {
        // `execution-failed` carries raw exception text and is filtered out in production; validation-coded errors are authored.
        setError(formatRoleTransitionErrors(result.errors, config.isProduction));
        return;
      }
      setResultSummary(isOffboard ? 'Provider offboard successful.' : 'Role transition successful.');
      setStepId('done');
      await onCompleted(sessionTargetRole);
      notify({
        title: isOffboard ? 'User deactivated' : 'Role transition successful',
        message: isOffboard
          ? `${practitionerName ?? 'This person'} has been deactivated.`
          : `${practitionerName ?? 'This person'} is now ${roleDisplayLabel(sessionTargetRole)}.`,
        type: isOffboard ? 'warning' : 'success',
      });
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'Failed to execute role transition'));
    } finally {
      setLoading(false);
    }
  };

  const ownSupervisorRows = impact?.ownSupervisorId
    ? [{ id: impact.ownSupervisorId }]
    : [];

  const ownSupervisorColumns: UnifyTableColumn<(typeof ownSupervisorRows)[number]>[] = [
    {
      key: 'supervisor',
      label: 'Supervisor',
      render: (row) => <Text size="sm">{displayNameFor(row.id)}</Text>,
      getValue: (row) => displayNameFor(row.id),
    },
    {
      key: 'onCloseout',
      label: 'On closeout',
      render: () => (
        <Text size="sm" c="dimmed">
          {isOffboard ? 'Link removed' : 'Unchanged'}
        </Text>
      ),
      getValue: () => (isOffboard ? 'removed' : 'unchanged'),
    },
  ];

  const superviseeColumns: UnifyTableColumn<(typeof supervisees)[number]>[] = [
    {
      key: 'supervisee',
      label: 'Supervisee',
      render: (row) => <Text size="sm">{displayNameFor(row.id, row.display)}</Text>,
      getValue: (row) => row.display ?? row.id,
    },
    {
      key: 'pendingCosigns',
      label: 'Pending cosigns',
      render: (row) => <Text size="sm">{row.pendingCosignCount}</Text>,
      getValue: (row) => String(row.pendingCosignCount),
    },
    {
      key: 'replacement',
      label: 'New supervisor',
      render: (row) => (
        <Select
          data={supervisorOptions}
          value={superviseeReplacements[row.id] ?? null}
          onChange={(v) => setSuperviseeReplacements((prev) => ({ ...prev, [row.id]: v ?? '' }))}
          placeholder="Required"
          error={!superviseeReplacements[row.id]}
          searchable
        />
      ),
      getValue: (row) => superviseeReplacements[row.id] ?? '',
    },
  ];

  const clientColumns: UnifyTableColumn<(typeof activeClients)[number]>[] = [
    {
      key: 'client',
      label: 'Client',
      render: (row) => {
        const showMedMgmtWarning =
          clientKind(row.episodeOfCareId) === 'discharge'
          && row.serviceType === 'individual'
          && Boolean(row.activeMedManagementProvider);
        const medMgmtName =
          row.activeMedManagementProvider?.display?.trim()
          || 'the Med Management provider';
        return (
          <Group gap={6} wrap="nowrap">
            <Text size="sm">{displayNameFor(row.patientId, row.patientDisplay)}</Text>
            {showMedMgmtWarning ? (
              <Tooltip
                label={`This client is also in Med Management. Please collaborate with ${medMgmtName} on this discharge.`}
                withArrow
                multiline
                maw={280}
              >
                <IconAlertTriangle
                  size={16}
                  color="var(--mantine-color-orange-6)"
                  aria-label="Med Management collaboration warning"
                />
              </Tooltip>
            ) : null}
          </Group>
        );
      },
      getValue: (row) => row.patientDisplay ?? row.patientId,
    },
    {
      key: 'disposition',
      label: 'Disposition',
      render: (row) => (
        <Select
          data={clientDispositionOptions}
          value={clientKind(row.episodeOfCareId)}
          onChange={(v) =>
            setClientKinds((prev) => ({
              ...prev,
              [row.episodeOfCareId]: (v as ClientDispositionKind) || 'transfer',
            }))
          }
        />
      ),
      getValue: (row) => clientKind(row.episodeOfCareId),
    },
    {
      key: 'replacement',
      label: 'New provider',
      render: (row) =>
        clientKind(row.episodeOfCareId) === 'transfer' ? (
          <Select
            data={providerOptions}
            value={clientReplacements[row.episodeOfCareId] ?? null}
            onChange={(v) =>
              setClientReplacements((prev) => ({ ...prev, [row.episodeOfCareId]: v ?? '' }))
            }
            placeholder="Required"
            error={!clientReplacements[row.episodeOfCareId]}
            searchable
          />
        ) : clientKind(row.episodeOfCareId) === 'matching-pool' ? (
          <Text size="sm" c="dimmed">
            Matching pool
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
      getValue: (row) => clientReplacements[row.episodeOfCareId] ?? '',
    },
  ];

  const appointmentColumns: UnifyTableColumn<(typeof futureAppointments)[number]>[] = isOffboard
    ? [
        {
          key: 'client',
          label: 'Client',
          render: (row) => <Text size="sm">{displayNameFor(row.patientId)}</Text>,
          getValue: (row) => row.patientId ?? '',
        },
        {
          key: 'start',
          label: 'Start',
          render: (row) => <Text size="sm">{row.start ? formatUsDateTime(row.start) : '—'}</Text>,
          getValue: (row) => row.start ?? '',
        },
        {
          key: 'disposition',
          label: 'Disposition',
          render: () => (
            <Text size="sm" c="dimmed">
              Cancel (all)
            </Text>
          ),
          getValue: () => 'cancel',
        },
      ]
    : [
    {
      key: 'client',
      label: 'Client',
      render: (row) => <Text size="sm">{displayNameFor(row.patientId)}</Text>,
      getValue: (row) => row.patientId ?? '',
    },
    {
      key: 'start',
      label: 'Start',
      render: (row) => <Text size="sm">{row.start ? formatUsDateTime(row.start) : '—'}</Text>,
      getValue: (row) => row.start ?? '',
    },
    {
      key: 'disposition',
      label: 'Disposition',
      render: (row) => (
        <Select
          data={APPOINTMENT_DISPOSITIONS}
          value={apptKind(row.id)}
          onChange={(v) =>
            setApptKinds((prev) => ({
              ...prev,
              [row.id]: (v as 'transfer' | 'reschedule' | 'cancel') || 'transfer',
            }))
          }
        />
      ),
      getValue: (row) => apptKind(row.id),
    },
    {
      key: 'replacement',
      label: 'New provider',
      render: (row) =>
        apptKind(row.id) === 'transfer' ? (
          <Select
            data={providerOptions}
            value={apptReplacements[row.id] ?? null}
            onChange={(v) => setApptReplacements((prev) => ({ ...prev, [row.id]: v ?? '' }))}
            placeholder="Required"
            error={!apptReplacements[row.id]}
            searchable
          />
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
      getValue: (row) => apptReplacements[row.id] ?? '',
    },
  ];

  const groupColumns: UnifyTableColumn<(typeof groupRoles)[number]>[] = [
    {
      key: 'group',
      label: 'Group',
      render: (row) => <Text size="sm">{row.groupDisplay ?? row.groupId}</Text>,
      getValue: (row) => row.groupDisplay ?? row.groupId,
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => (
        <Badge variant="light" size="sm">
          {row.role === 'primary-facilitator' ? 'Primary facilitator' : 'Facilitator'}
        </Badge>
      ),
      getValue: (row) => row.role,
    },
    {
      key: 'action',
      label: 'Action',
      render: (row) => (
        <Select
          data={
            row.role === 'primary-facilitator'
              ? [{ value: 'replace', label: 'Replace primary' }]
              : [
                  { value: 'replace', label: 'Replace' },
                  { value: 'remove', label: 'Remove from group' },
                ]
          }
          value={groupActionFor(row.groupId, row.role)}
          onChange={(v) =>
            setGroupActions((prev) => ({ ...prev, [row.groupId]: (v as 'replace' | 'remove') || 'replace' }))
          }
        />
      ),
      getValue: (row) => groupActionFor(row.groupId, row.role),
    },
    {
      key: 'replacement',
      label: 'New facilitator',
      render: (row) =>
        groupActionFor(row.groupId, row.role) === 'replace' ? (
          <Select
            data={providerOptions}
            value={groupReplacements[row.groupId] ?? null}
            onChange={(v) => setGroupReplacements((prev) => ({ ...prev, [row.groupId]: v ?? '' }))}
            placeholder="Required"
            error={!groupReplacements[row.groupId]}
            searchable
          />
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
      getValue: (row) => groupReplacements[row.groupId] ?? '',
    },
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        isOffboard
          ? `Deactivate user${practitionerName ? `: ${practitionerName}` : ''}`
          : isReactivate
            ? `Reactivate user${practitionerName ? `: ${practitionerName}` : ''}`
            : `Role transition${practitionerName ? `: ${practitionerName}` : ''}`
      }
      size="1000px"
      centered
    >
      <Stack gap="lg">
        {error ? <Alert color="red">{error}</Alert> : null}

        <Stepper
          active={activeIndex}
          onStepClick={(index) => {
            if (index < activeIndex) setStepId(stepIds[index]);
          }}
          allowNextStepsSelect={false}
          size="sm"
          wrap={false}
        >
          <Stepper.Step label="Review" />
          {!isReactivate ? <Stepper.Step label="Impact" /> : null}
          {!isReactivate && needsSupervisionStep ? <Stepper.Step label="Supervision" /> : null}
          {!isReactivate && needsCaseloadStep ? <Stepper.Step label="Caseload" /> : null}
          <Stepper.Step label="Confirm" />
        </Stepper>

        {stepId === 'review' ? (
          <Stack gap="md">
            {isReactivate ? (
              <>
                <Alert color="teal" title="Reactivate account">
                  This practitioner is inactive. Choose a role to restore login and permissions. Caseload
                  and historical AccessPolicy rows are not auto-restored.
                </Alert>
                <Select
                  label="Role to assign"
                  placeholder="Select a role"
                  data={assignableRoles.map((r) => ({ value: r, label: roleDisplayLabel(r) }))}
                  value={reactivateRole}
                  onChange={(value) => setReactivateRole((value as Role) ?? null)}
                  searchable
                  required
                />
                <Group justify="flex-end">
                  <Button variant="default" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    disabled={!reactivateRole}
                    onClick={() => setStepId('confirm')}
                  >
                    Continue
                  </Button>
                </Group>
              </>
            ) : (
              <>
            {isOffboard ? (
              <Alert color="orange" title="Deactivate / offboard">
                All roles will be cleared and the account deactivated after clinical closeout.
              </Alert>
            ) : null}

            <Group gap="xs">
              <Text size="sm" fw={500}>
                {isOffboard ? 'Account after closeout:' : 'Primary role after change:'}
              </Text>
              <Badge size="lg" variant="light">
                {primaryAfterLabel}
              </Badge>
            </Group>

            {removedRoles.length > 0 ? (
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  Removing:
                </Text>
                {removedRoles.map((r) => (
                  <Badge key={r} size="lg" color="red" variant="light">
                    {roleDisplayLabel(r)}
                  </Badge>
                ))}
              </Group>
            ) : null}

            {addedRoles.length > 0 ? (
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  Adding:
                </Text>
                {addedRoles.map((r) => (
                  <Badge key={r} size="lg" color="teal" variant="light">
                    {roleDisplayLabel(r)}
                  </Badge>
                ))}
              </Group>
            ) : null}

            {leavingTreating ? (
              <Alert color="orange" title="Ends treating clients">
                Active episodes, future appointments, and group facilitator slots must be transferred
                or discharged before this change can apply.
              </Alert>
            ) : null}

            {leavingSupervising ? (
              <Alert color="orange" title="Ends supervising practitioners">
                Every supervisee must be reassigned to another supervisor. Pending cosign work moves
                with them; note authorship is never rewritten.
              </Alert>
            ) : null}

            {!leavingTreating && !leavingSupervising ? (
              <Alert color="blue" title="Caseload stays put">
                Clinical capabilities are unchanged, so episodes and appointments are not moved.
              </Alert>
            ) : null}

            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                loading={loading}
                onClick={() => void loadImpact()}
              >
                Review impact
              </Button>
            </Group>
              </>
            )}
          </Stack>
        ) : null}

        {stepId === 'impact' ? (
          <Stack gap="md">
            {!impact ? (
              <Text size="sm">Loading impact…</Text>
            ) : (
              <>
                {affectedImpactCounts.length > 0 ? (
                  <Group gap="xl">
                    {affectedImpactCounts.map((item) => (
                      <Text key={item.label} size="sm">
                        {item.label}: {item.count}
                      </Text>
                    ))}
                  </Group>
                ) : null}
                {leavingTreating && impact.ownSupervisorId ? (
                  <Text size="sm" c="dimmed">
                    Own supervisor link will be cleared.
                  </Text>
                ) : null}

                {(impact.recordsRequiringManualResolution?.length ?? 0) > 0 ? (
                  <Alert color="orange" title="Items requiring resolution">
                    <Stack gap={4}>
                      {impact.recordsRequiringManualResolution.map((line) => (
                        <Text key={line} size="sm">
                          • {line}
                        </Text>
                      ))}
                    </Stack>
                  </Alert>
                ) : null}

                {closeoutMessage ? (
                  <Alert color="red" title="Closeout required before role change">
                    {closeoutMessage}
                  </Alert>
                ) : (
                  <Alert color="green" title="No clinical closeout required">
                    No active caseload or supervisee relationships block this change.
                  </Alert>
                )}
              </>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => goToStep(-1)}>
                Back
              </Button>
              <Button disabled={!impact} onClick={() => goToStep(1)}>
                Continue
              </Button>
            </Group>
          </Stack>
        ) : null}

        {stepId === 'supervision' ? (
          <Stack gap="md">
            {isOffboard ? (
              <Alert color="blue">
                The practitioner&apos;s supervisor link is cleared when the account is deactivated.
                {losingSupervision
                  ? ' Supervisees must still be reassigned before closeout completes.'
                  : null}
              </Alert>
            ) : (
              <Alert color="orange">
                Reassign every supervisee before supervisor access is removed.
              </Alert>
            )}
            <UnifyTable
              columns={ownSupervisorColumns}
              data={ownSupervisorRows}
              getRowKey={(row) => row.id}
              withPaper={false}
              title="Supervisor"
              emptyMessage="No supervisor assigned"
            />
            {needsSuperviseeReassign ? (
              <>
                <UnifyTable
                  columns={superviseeColumns}
                  data={supervisees}
                  getRowKey={(row) => row.id}
                  withPaper={false}
                  title="Supervisees"
                  headerRight={
                    <Select
                      data={supervisorOptions}
                      placeholder="Set all to…"
                      value={null}
                      onChange={(v) => {
                        if (!v) return;
                        setSuperviseeReplacements(bulkSet(supervisees.map((s) => s.id), v));
                      }}
                      searchable
                      w={240}
                    />
                  }
                />
                {missingSuperviseeReplacements > 0 ? (
                  <Text size="sm" c="red">
                    {missingSuperviseeReplacements} supervisee(s) still need a new supervisor.
                  </Text>
                ) : null}
              </>
            ) : null}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => goToStep(-1)}>
                Back
              </Button>
              <Button disabled={missingSuperviseeReplacements > 0} onClick={() => goToStep(1)}>
                Continue
              </Button>
            </Group>
          </Stack>
        ) : null}

        {stepId === 'caseload' ? (
          <Stack gap="lg">
            <Alert color="orange" title="Caseload closeout">
              Historical appointments, signed notes, and authorship are never changed. Only future
              actionable work is moved.
            </Alert>

            {activeClients.length > 0 ? (
              <UnifyTable
                columns={clientColumns}
                data={activeClients}
                getRowKey={(row) => row.episodeOfCareId}
                withPaper={false}
                title="Clients"
                headerRight={
                  <Group gap="sm" wrap="nowrap">
                    <Select
                      data={clientDispositionOptions}
                      placeholder="Set all dispositions"
                      value={null}
                      onChange={(v) => {
                        if (!v) return;
                        setClientKinds(
                          bulkSet(
                            activeClients.map((c) => c.episodeOfCareId),
                            v as ClientDispositionKind,
                          ),
                        );
                      }}
                      w={190}
                    />
                    <Select
                      data={providerOptions}
                      placeholder="Set all providers"
                      value={null}
                      onChange={(v) => {
                        if (!v) return;
                        setClientReplacements(
                          bulkSet(activeClients.map((c) => c.episodeOfCareId), v),
                        );
                      }}
                      searchable
                      w={220}
                    />
                  </Group>
                }
              />
            ) : null}

            {futureAppointments.length > 0 ? (
              <UnifyTable
                columns={appointmentColumns}
                data={futureAppointments}
                getRowKey={(row) => row.id}
                withPaper={false}
                title={
                  isOffboard
                    ? `${futureAppointments.length} future appointment(s) will be cancelled`
                    : 'Future appointments'
                }
                headerRight={
                  isOffboard ? undefined : (
                  <Group gap="sm" wrap="nowrap">
                    <Select
                      data={APPOINTMENT_DISPOSITIONS}
                      placeholder="Set all dispositions"
                      value={null}
                      onChange={(v) => {
                        if (!v) return;
                        setApptKinds(
                          bulkSet(
                            futureAppointments.map((a) => a.id),
                            v as 'transfer' | 'reschedule' | 'cancel',
                          ),
                        );
                      }}
                      w={190}
                    />
                    <Select
                      data={providerOptions}
                      placeholder="Set all providers"
                      value={null}
                      onChange={(v) => {
                        if (!v) return;
                        setApptReplacements(bulkSet(futureAppointments.map((a) => a.id), v));
                      }}
                      searchable
                      w={220}
                    />
                  </Group>
                  )
                }
              />
            ) : null}

            {groupRoles.length > 0 ? (
              <UnifyTable
                columns={groupColumns}
                data={groupRoles}
                getRowKey={(row) => row.groupId}
                withPaper={false}
                title="Group facilitator roles"
                headerRight={
                  <Group gap="sm" wrap="nowrap">
                    <Select
                      data={[
                        { value: 'replace', label: 'Replace' },
                        { value: 'remove', label: 'Remove from group' },
                      ]}
                      placeholder="Set all actions"
                      value={null}
                      onChange={(v) => {
                        if (!v) return;
                        setGroupActions(
                          Object.fromEntries(
                            groupRoles.map((g) => [
                              g.groupId,
                              // Primary facilitator slots cannot be left empty.
                              g.role === 'primary-facilitator' ? 'replace' : (v as 'replace' | 'remove'),
                            ]),
                          ),
                        );
                      }}
                      w={190}
                    />
                    <Select
                      data={providerOptions}
                      placeholder="Set all facilitators"
                      value={null}
                      onChange={(v) => {
                        if (!v) return;
                        setGroupReplacements(bulkSet(groupRoles.map((g) => g.groupId), v));
                      }}
                      searchable
                      w={220}
                    />
                  </Group>
                }
              />
            ) : null}

            {(impact?.activeSchedules?.length ?? 0) > 0 ? (
              <Text size="sm" c="dimmed">
                {impact?.activeSchedules?.length} schedule(s) will be set inactive automatically.
              </Text>
            ) : null}

            {missingCaseloadReplacements > 0 ? (
              <Text size="sm" c="red">
                {missingCaseloadReplacements} row(s) still need a replacement before continuing.
              </Text>
            ) : null}

            <Group justify="flex-end">
              <Button variant="default" onClick={() => goToStep(-1)}>
                Back
              </Button>
              <Button disabled={missingCaseloadReplacements > 0} onClick={() => goToStep(1)}>
                Continue
              </Button>
            </Group>
          </Stack>
        ) : null}

        {stepId === 'confirm' ? (
          <Stack gap="md">
            {isReactivate ? (
              <>
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    Role to assign:
                  </Text>
                  <Badge size="lg" variant="light">
                    {primaryAfterLabel}
                  </Badge>
                </Group>
                <Alert color="blue">
                  Login access and AccessPolicy for this role will be restored. Caseload is not
                  automatically reassigned.
                </Alert>
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => goToStep(-1)}>
                    Back
                  </Button>
                  <Button
                    color="teal"
                    loading={loading}
                    onClick={() => void runReactivate()}
                  >
                    Reactivate user
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    Primary role:
                  </Text>
                  <Badge size="lg" variant="light">
                    {primaryAfterLabel}
                  </Badge>
                </Group>
                <Text size="sm">
                  Roles after change: {formatCollapsedRoleLabels(heldAfter) || 'None'}
                </Text>
                <Text size="sm">Treats clients: {treatsClients ? 'Yes' : 'No'}</Text>
                <Text size="sm">Supervises practitioners: {supervises ? 'Yes' : 'No'}</Text>
                {closeoutMessage ? (
                  <Alert color="red">Closeout dispositions run before the role change is applied.</Alert>
                ) : null}
                <Alert color="blue">
                  Historical appointments, notes, signatures, and authorship are never rewritten.
                </Alert>
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => goToStep(-1)}>
                    Back
                  </Button>
                  <Button
                    loading={loading}
                    onClick={() => void runExecute()}
                  >
                    Confirm and apply
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        ) : null}

        {stepId === 'done' ? (
          <Stack gap="md">
            <Alert color="green">{resultSummary ?? 'Role transition successful.'}</Alert>
            <Group justify="flex-end">
              <Button onClick={onClose}>Done</Button>
            </Group>
          </Stack>
        ) : null}
      </Stack>
    </Modal>
  );
}
